import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BINS, POSTERS } from "./swachhataContent";
import "./SwachhataSideArt.css";

/*
 * Side columns for the public student page (/swachhata-hi-seva) — the green
 * margins left and right of the welcome / quiz / result card.
 *   left  — the four bins catching waste, and a rotating card saying what goes
 *           in which bin (examples from BINS)
 *   right — a sapling that grows a leaf for every answered question, the six
 *           posters, and the public SWM events link
 * Only shown where the viewport leaves room (SwachhataSideArt.css).
 */

const ITEMS = [
  <ellipse key="leaf" rx="7" ry="3.6" fill="#a6ee74" />,
  <g key="bottle">
    <rect x="-3.4" y="-8" width="6.8" height="15" rx="2.2" fill="#c6e6ff" />
    <rect x="-1.8" y="-10.8" width="3.6" height="3.4" rx="1" fill="#6db3f2" />
  </g>,
  <rect key="pad" x="-5.5" y="-4.5" width="11" height="9" rx="2.4" fill="#fff" stroke="#f4b5b5" strokeWidth="1.2" />,
  <g key="battery">
    <rect x="-3.8" y="-7" width="7.6" height="14" rx="1.6" fill="#ffd166" />
    <rect x="-1.6" y="-9.2" width="3.2" height="2.4" fill="#d99a00" />
  </g>,
];

// Leaf spots up the stem (x, y, rotation) — alternating sides, bottom to top.
const LEAF_SPOTS = [
  [99, 150, -155], [100, 140, -25], [100, 129, -160], [101, 118, -20], [102, 107, -155],
  [102, 96, -25], [101, 85, -150], [100, 75, -30], [100, 66, -125], [100, 62, -55],
];
const STEM = 118;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Bins() {
  return (
    <svg className="shsa-bins__svg" viewBox="0 0 250 150">
      <ellipse cx="125" cy="139" rx="118" ry="7" fill="rgba(20,70,30,0.18)" />
      {BINS.map((b, k) => (
        <g key={b.key} transform={`translate(${12 + k * 58} 0)`} style={{ "--d": `${k * 1.2}s` }}>
          <g transform="translate(22 70)">
            <g className="shsa-item">{ITEMS[k]}</g>
          </g>
          <g className="shsa-bin">
            <g className="shsa-lid">
              <rect x="15" y="62" width="14" height="6" rx="2" fill={b.hex} />
              <rect x="-2" y="67" width="48" height="9" rx="3.5" fill={b.hex} />
            </g>
            <path d="M1 78h42l-4.5 58h-33Z" fill={b.hex} />
            <path d="M1 78h42l-0.6 7H1.6Z" fill="rgba(0,0,0,0.16)" />
            <g className="shsa-eyes">
              <circle cx="15" cy="101" r="5" fill="#fff" />
              <circle cx="29" cy="101" r="5" fill="#fff" />
              <circle cx="15.6" cy="102" r="2.4" fill="#16261c" />
              <circle cx="29.6" cy="102" r="2.4" fill="#16261c" />
            </g>
            <path d="M16 113q6 5 12 0" stroke="#fff" strokeWidth="2.4" fill="none" strokeLinecap="round" />
          </g>
        </g>
      ))}
    </svg>
  );
}

export default function SwachhataSideArt({ phase, answered = 0, total = 10 }) {
  const [tip, setTip] = useState(0);
  const [poster, setPoster] = useState(0);

  useEffect(() => {
    if (reducedMotion()) return undefined;
    const a = setInterval(() => setTip((i) => (i + 1) % BINS.length), 5000);
    const b = setInterval(() => setPoster((i) => (i + 1) % POSTERS.length), 4200);
    return () => {
      clearInterval(a);
      clearInterval(b);
    };
  }, []);

  const bin = BINS[tip];
  const done = phase === "result";
  const ratio = done ? 1 : phase === "quiz" ? Math.min(1, answered / Math.max(1, total)) : 0;
  const leaves = Math.round(ratio * LEAF_SPOTS.length);
  const caption = done
    ? "Well done! Your plant is fully grown."
    : phase === "quiz"
      ? `${answered} of ${total} answered. Your plant grows a leaf with every answer.`
      : "Answer the quiz questions to grow this plant.";

  return (
    <>
      <aside className="shsa-side shsa-side--left" aria-hidden="true">
        <div className="shsa-card shsa-bins">
          <span className="shsa-kicker">Which bin?</span>
          <Bins />
          <ul className="shsa-bins__labels">
            {BINS.map((b) => (
              <li key={b.key} className={b.key === bin.key ? "is-on" : ""} style={{ "--c": b.hex }}>
                <i />
                {b.colour}
              </li>
            ))}
          </ul>
        </div>

        <div key={bin.key} className="shsa-card shsa-tip" style={{ "--c": bin.hex }}>
          <span className="shsa-kicker shsa-kicker--dark">Did you know?</span>
          <span className="shsa-tip__head">
            <i />
            {`${bin.colour} · ${bin.name}`}
          </span>
          <p className="shsa-tip__text">{bin.examples}</p>
        </div>
      </aside>

      <aside className="shsa-side shsa-side--right">
        <div className={`shsa-card shsa-tree ${done ? "is-done" : ""}`} aria-hidden="true">
          <svg className="shsa-tree__svg" viewBox="0 0 200 200">
            <g className="shsa-tree__rays">
              {Array.from({ length: 8 }, (_, k) => (
                <path key={k} d="M160 12v-7" stroke="#ffc53d" strokeWidth="3" strokeLinecap="round" transform={`rotate(${k * 45} 160 36)`} />
              ))}
            </g>
            <circle cx="160" cy="36" r="14" fill="#ffc53d" />
            <ellipse cx="100" cy="188" rx="64" ry="6" fill="rgba(0,0,0,0.12)" />
            <g className="shsa-tree__plant">
              <path className="shsa-tree__stem" d="M100 164 C 97 138, 104 110, 100 58" strokeDasharray={STEM} style={{ strokeDashoffset: STEM * (1 - Math.max(0.16, ratio)) }} />
              {LEAF_SPOTS.map(([x, y, r], k) => (
                <g key={k} transform={`translate(${x} ${y}) rotate(${r})`}>
                  <path className={`shsa-tree__leaf ${k < leaves ? "is-on" : ""}`} d="M0 0C8-9 22-10 30 0C22 10 8 9 0 0Z" fill={k % 2 ? "#4cbf5f" : "#2f9a47"} />
                </g>
              ))}
              <g className="shsa-tree__flower">
                {[0, 72, 144, 216, 288].map((a) => (
                  <ellipse key={a} cx="100" cy="47" rx="5" ry="9" fill="#ff8fab" transform={`rotate(${a} 100 56)`} />
                ))}
                <circle cx="100" cy="56" r="5" fill="#ffc53d" />
              </g>
            </g>
            <path d="M62 166h76l-9 24H71Z" fill="#c2703a" />
            <rect x="57" y="158" width="86" height="12" rx="4" fill="#d9844a" />
          </svg>
          <span className="shsa-tree__caption">{caption}</span>
        </div>

        <div className="shsa-card shsa-posters" aria-hidden="true">
          <span className="shsa-kicker">Posters</span>
          <div className="shsa-posters__stack">
            {POSTERS.map((p, k) => (
              <img
                key={p.key}
                src={p.src}
                alt=""
                className={k === poster ? "is-on" : k === (poster + POSTERS.length - 1) % POSTERS.length ? "is-prev" : ""}
              />
            ))}
          </div>
          <span key={poster} className="shsa-posters__title">
            {POSTERS[poster].title}
          </span>
        </div>

        <Link to="/swachhata-hi-seva/events" className="shsa-card shsa-link">
          <span className="shsa-link__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="3" />
              <circle cx="9" cy="10" r="2" />
              <path d="m21 15-5-5-8 9" />
            </svg>
          </span>
          <span className="shsa-link__text">
            <b>SWM Events</b>
            <small>See what schools did</small>
          </span>
          <span className="shsa-link__arrow" aria-hidden="true">
            →
          </span>
        </Link>
      </aside>
    </>
  );
}
